import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Search,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  UserCheck,
  Trash2,
  Loader2,
  Info,
  RefreshCw,
  CreditCard,
} from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { formatLocalDate } from '@/lib/utils';

interface UnidentifiedPayment {
  id: number;
  referencia: string;
  concepto: string | null;
  amount: string;
  fechaPago: string;
  bankAccountId: number | null;
  source: string;
  reason: string | null;
  status: 'pending' | 'resolved' | 'discarded';
  resolvedByUserId: number | null;
  resolvedAt: string | null;
  notes: string | null;
  createdAt: string;
}

interface StudentResult {
  id: number;
  nombre: string;
  apellidoPaterno: string;
  numeroIdentificacionUnico: string;
}

interface OpenInvoice {
  id: number;
  invoiceNumber: string;
  date: string;
  dueDate: string;
  total: string;
  totalPaid: string;
  pendingAmount: string;
}

interface BankAccount {
  id: number;
  name: string;
  code: string;
}

const STATUS_CONFIG = {
  pending: { label: 'Pendiente', color: 'bg-orange-100 text-orange-800', icon: AlertTriangle },
  resolved: { label: 'Resuelto', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  discarded: { label: 'Descartado', color: 'bg-gray-100 text-gray-600', icon: XCircle },
};

function fmt(n: number | string) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n));
}

async function fetchUnidentified(status: string, search: string) {
  const p = new URLSearchParams({ status, limit: '200' });
  if (search) p.append('search', search);
  const res = await fetch(`/api/treasury/unidentified-payments?${p}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Error al cargar pagos no identificados');
  return res.json() as Promise<{ data: UnidentifiedPayment[]; total: number }>;
}

async function searchAlumnos(q: string): Promise<StudentResult[]> {
  if (!q || q.length < 2) return [];
  const res = await fetch(`/api/alumnos?search=${encodeURIComponent(q)}&limit=20`, { credentials: 'include' });
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data) ? data : (data.alumnos ?? data.data ?? []);
}

async function fetchStudentInvoices(alumnoId: number): Promise<OpenInvoice[]> {
  const res = await fetch(`/api/treasury/unidentified-payments/student-invoices?alumnoId=${alumnoId}`, { credentials: 'include' });
  if (!res.ok) return [];
  return res.json();
}

async function fetchBankAccounts(): Promise<BankAccount[]> {
  const res = await fetch('/api/administration/payment-accounts', { credentials: 'include' });
  if (!res.ok) return [];
  return res.json();
}

export default function UnidentifiedPayments() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: result, isLoading, refetch } = useQuery({
    queryKey: ['unidentified-payments', statusFilter, debouncedSearch],
    queryFn: () => fetchUnidentified(statusFilter, debouncedSearch),
  });

  const { data: bankAccounts = [] } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: fetchBankAccounts,
  });

  const list = result?.data ?? [];
  const total = result?.total ?? 0;
  const pendingCount = list.filter(r => r.status === 'pending').length;

  // ── Assign modal state ─────────────────────────────────────────────────────
  const [assignTarget, setAssignTarget] = useState<UnidentifiedPayment | null>(null);
  const [alumnoSearch, setAlumnoSearch] = useState('');
  const [alumnoResults, setAlumnoResults] = useState<StudentResult[]>([]);
  const [alumnoLoading, setAlumnoLoading] = useState(false);
  const [selectedAlumno, setSelectedAlumno] = useState<StudentResult | null>(null);
  const [studentInvoices, setStudentInvoices] = useState<OpenInvoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | 'credit' | null>(null);
  const [overrideBankAccountId, setOverrideBankAccountId] = useState<number | null>(null);
  const [assignNotes, setAssignNotes] = useState('');

  // ── Discard modal state ────────────────────────────────────────────────────
  const [discardTarget, setDiscardTarget] = useState<UnidentifiedPayment | null>(null);
  const [discardNotes, setDiscardNotes] = useState('');

  const openAssign = (up: UnidentifiedPayment) => {
    setAssignTarget(up);
    setAlumnoSearch('');
    setAlumnoResults([]);
    setSelectedAlumno(null);
    setStudentInvoices([]);
    setSelectedInvoiceId(null);
    setOverrideBankAccountId(up.bankAccountId);
    setAssignNotes('');
  };

  const openDiscard = (up: UnidentifiedPayment) => {
    setDiscardTarget(up);
    setDiscardNotes('');
  };

  // Alumno search debounce
  useEffect(() => {
    if (alumnoSearch.length < 2) { setAlumnoResults([]); return; }
    setAlumnoLoading(true);
    const t = setTimeout(async () => {
      const res = await searchAlumnos(alumnoSearch);
      setAlumnoResults(res);
      setAlumnoLoading(false);
    }, 350);
    return () => clearTimeout(t);
  }, [alumnoSearch]);

  const selectAlumno = useCallback(async (a: StudentResult) => {
    setSelectedAlumno(a);
    setAlumnoResults([]);
    setAlumnoSearch('');
    setSelectedInvoiceId(null);
    setLoadingInvoices(true);
    const invs = await fetchStudentInvoices(a.id);
    setStudentInvoices(invs);
    setLoadingInvoices(false);
  }, []);

  const assignMutation = useMutation({
    mutationFn: async () => {
      if (!assignTarget || !selectedAlumno) throw new Error('Selecciona un alumno');
      const asCredit = selectedInvoiceId === 'credit';
      if (!asCredit && !selectedInvoiceId) throw new Error('Selecciona una factura o "Guardar como Saldo a Favor"');
      const res = await fetch(`/api/treasury/unidentified-payments/${assignTarget.id}/assign`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          alumnoId: selectedAlumno.id,
          invoiceId: asCredit ? undefined : selectedInvoiceId,
          asCredit,
          bankAccountId: overrideBankAccountId || undefined,
          notes: assignNotes || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al asignar');
      return data;
    },
    onSuccess: () => {
      toast({ title: 'Pago asignado', description: 'El pago ha sido aplicado correctamente.' });
      queryClient.invalidateQueries({ queryKey: ['unidentified-payments'] });
      setAssignTarget(null);
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const discardMutation = useMutation({
    mutationFn: async () => {
      if (!discardTarget) return;
      const res = await fetch(`/api/treasury/unidentified-payments/${discardTarget.id}/discard`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: discardNotes || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al descartar');
      return data;
    },
    onSuccess: () => {
      toast({ title: 'Descartado', description: 'El registro fue descartado.' });
      queryClient.invalidateQueries({ queryKey: ['unidentified-payments'] });
      setDiscardTarget(null);
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  return (
    <div className="space-y-5">
      {/* Header info */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Aquí aparecen los pagos del CSV masivo que <strong>no pudieron conciliarse automáticamente</strong> (referencia inválida, alumno no encontrado, sin facturas pendientes). Puedes asignarlos manualmente a un alumno o descartarlos.
        </AlertDescription>
      </Alert>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              Pagos No Identificados
              {statusFilter === 'pending' && pendingCount > 0 && (
                <Badge className="bg-orange-100 text-orange-800 border-0">{pendingCount} pendientes</Badge>
              )}
              {statusFilter !== 'pending' && (
                <Badge variant="outline">{total} registros</Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                  <SelectItem value="resolved">Resueltos</SelectItem>
                  <SelectItem value="discarded">Descartados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por referencia, concepto o motivo..."
              className="pl-10"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
              <p className="text-sm text-gray-500 mt-2">Cargando...</p>
            </div>
          ) : list.length === 0 ? (
            <div className="p-10 text-center">
              <CheckCircle2 className="w-10 h-10 mx-auto text-green-400 mb-2" />
              <p className="text-gray-500 font-medium">
                {statusFilter === 'pending' ? 'Sin pagos pendientes de identificar' : 'Sin resultados'}
              </p>
              <p className="text-xs text-gray-400 mt-1">Los pagos no identificados del CSV masivo aparecerán aquí</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3 text-left">Fecha</th>
                    <th className="px-4 py-3 text-left">Referencia</th>
                    <th className="px-4 py-3 text-left">Concepto / Motivo</th>
                    <th className="px-4 py-3 text-right">Monto</th>
                    <th className="px-4 py-3 text-left">Origen</th>
                    <th className="px-4 py-3 text-left">Estado</th>
                    <th className="px-4 py-3 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(up => {
                    const cfg = STATUS_CONFIG[up.status];
                    const Icon = cfg.icon;
                    return (
                      <tr key={up.id} className="border-t hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600 font-mono text-xs">{up.fechaPago}</td>
                        <td className="px-4 py-3 whitespace-nowrap font-mono font-medium">{up.referencia}</td>
                        <td className="px-4 py-3 max-w-[260px]">
                          {up.concepto && (
                            <p className="truncate text-gray-700" title={up.concepto}>{up.concepto}</p>
                          )}
                          {up.reason && (
                            <p className="truncate text-xs text-orange-600 mt-0.5" title={up.reason}>{up.reason}</p>
                          )}
                          {up.notes && (
                            <p className="truncate text-xs text-gray-400 mt-0.5" title={up.notes}>Nota: {up.notes}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-green-700 whitespace-nowrap">
                          {fmt(up.amount)}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="text-xs">{up.source}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                            <Icon className="h-3 w-3" />
                            {cfg.label}
                          </span>
                          {up.resolvedAt && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              {formatLocalDate(up.resolvedAt, { month: 'short', day: 'numeric' })}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {up.status === 'pending' && (
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => openAssign(up)}
                                title="Asignar a alumno"
                              >
                                <UserCheck className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-gray-400 hover:text-red-600 hover:bg-red-50"
                                onClick={() => openDiscard(up)}
                                title="Descartar"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── ASSIGN MODAL ─────────────────────────────────────────────────── */}
      <Dialog open={!!assignTarget} onOpenChange={open => { if (!open) setAssignTarget(null); }}>
        <DialogContent className="max-w-2xl w-full flex flex-col max-h-[92vh]">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-blue-600" />
              Asignar Pago a Alumno
            </DialogTitle>
          </DialogHeader>

          {assignTarget && (
            <>
              {/* Payment summary — always visible, outside scroll container */}
              <div className="shrink-0 rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm">
                <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Referencia:</span>
                    <span className="font-mono font-semibold">{assignTarget.referencia}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Fecha:</span>
                    <span>{assignTarget.fechaPago}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Monto:</span>
                    <span className="font-bold text-green-700 text-base">{fmt(assignTarget.amount)}</span>
                  </div>
                  {assignTarget.concepto && (
                    <div className="flex justify-between gap-2 col-span-2">
                      <span className="text-gray-500 shrink-0">Concepto:</span>
                      <span className="truncate text-right text-gray-700">{assignTarget.concepto}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Step 1: Search student — outside scroll so dropdown is never clipped */}
              <div className="shrink-0 space-y-1.5">
                <label className="text-sm font-semibold">1. Buscar alumno</label>
                {selectedAlumno ? (
                  <div className="flex items-center justify-between rounded-md border bg-green-50 px-3 py-2.5">
                    <div>
                      <p className="font-semibold text-sm">{selectedAlumno.nombre} {selectedAlumno.apellidoPaterno}</p>
                      <p className="text-xs text-gray-500">{selectedAlumno.numeroIdentificacionUnico}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedAlumno(null); setStudentInvoices([]); setSelectedInvoiceId(null); }}>
                      Cambiar
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      className="pl-9 h-10 text-base"
                      placeholder="Escribe nombre, apellido o NUI..."
                      value={alumnoSearch}
                      onChange={e => setAlumnoSearch(e.target.value)}
                      autoFocus
                    />
                    {alumnoLoading && (
                      <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-gray-400" />
                    )}
                    {alumnoResults.length > 0 && (
                      <div className="absolute z-[200] w-full mt-1 rounded-md border bg-white shadow-xl max-h-56 overflow-y-auto">
                        {alumnoResults.map(a => (
                          <button
                            key={a.id}
                            className="w-full text-left px-4 py-2.5 hover:bg-blue-50 transition-colors border-b last:border-0"
                            onClick={() => selectAlumno(a)}
                          >
                            <p className="font-medium text-sm">{a.nombre} {a.apellidoPaterno}</p>
                            <p className="text-xs text-gray-500">{a.numeroIdentificacionUnico}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Step 2 + extras — scrollable section */}
              <div className="overflow-y-auto flex-1 space-y-4 pr-1">
                {selectedAlumno && (
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold">2. ¿Cómo aplicar el pago?</label>
                    {loadingInvoices ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> Cargando facturas...
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {studentInvoices.length > 0 ? (
                          studentInvoices.map(inv => (
                            <label
                              key={inv.id}
                              className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${selectedInvoiceId === inv.id ? 'border-blue-500 bg-blue-50' : 'hover:border-gray-300'}`}
                            >
                              <input
                                type="radio"
                                name="invoice"
                                value={inv.id}
                                checked={selectedInvoiceId === inv.id}
                                onChange={() => setSelectedInvoiceId(inv.id)}
                                className="shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-mono font-medium text-sm">{inv.invoiceNumber}</p>
                                <p className="text-xs text-gray-500">Vence: {inv.dueDate}</p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="font-semibold text-orange-700 text-sm">{fmt(inv.pendingAmount)}</p>
                                <p className="text-xs text-gray-400">pendiente</p>
                              </div>
                            </label>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 italic">Sin facturas pendientes — solo se puede guardar como Saldo a Favor</p>
                        )}

                        {/* Option: Save as credit */}
                        <label className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${selectedInvoiceId === 'credit' ? 'border-violet-500 bg-violet-50' : 'hover:border-gray-300'}`}>
                          <input
                            type="radio"
                            name="invoice"
                            value="credit"
                            checked={selectedInvoiceId === 'credit'}
                            onChange={() => setSelectedInvoiceId('credit')}
                            className="shrink-0"
                          />
                          <CreditCard className="h-4 w-4 text-violet-600 shrink-0" />
                          <div>
                            <p className="font-medium text-sm text-violet-700">Guardar como Saldo a Favor</p>
                            <p className="text-xs text-gray-500">El alumno podrá usarlo en futuros pagos</p>
                          </div>
                        </label>
                      </div>
                    )}
                  </div>
                )}

                {/* Bank account override */}
                {selectedAlumno && selectedInvoiceId !== null && bankAccounts.length > 1 && (
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold">Cuenta bancaria de destino</label>
                    <select
                      className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                      value={overrideBankAccountId ?? ''}
                      onChange={e => setOverrideBankAccountId(Number(e.target.value) || null)}
                    >
                      <option value="">-- Seleccionar --</option>
                      {bankAccounts.map(a => (
                        <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Notes */}
                {selectedAlumno && selectedInvoiceId !== null && (
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold">Notas (opcional)</label>
                    <Input
                      placeholder="Observaciones sobre este pago..."
                      value={assignNotes}
                      onChange={e => setAssignNotes(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          <DialogFooter className="shrink-0 pt-3 border-t">
            <Button variant="outline" onClick={() => setAssignTarget(null)}>Cancelar</Button>
            <Button
              onClick={() => assignMutation.mutate()}
              disabled={assignMutation.isPending || !selectedAlumno || selectedInvoiceId === null}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {assignMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Aplicando...</>
              ) : (
                <><CheckCircle2 className="h-4 w-4 mr-2" /> Aplicar Pago</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── DISCARD MODAL ────────────────────────────────────────────────── */}
      <Dialog open={!!discardTarget} onOpenChange={open => { if (!open) setDiscardTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" />
              Descartar Pago No Identificado
            </DialogTitle>
          </DialogHeader>
          {discardTarget && (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Estás por descartar el pago de <strong>{fmt(discardTarget.amount)}</strong> (Ref: <strong>{discardTarget.referencia}</strong>). Esta acción no registra ningún pago en el sistema. Solo marca el registro como ignorado.
                </AlertDescription>
              </Alert>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Motivo del descarte (opcional)</label>
                <Input
                  placeholder="Ej: Pago duplicado, error del banco..."
                  value={discardNotes}
                  onChange={e => setDiscardNotes(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscardTarget(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              onClick={() => discardMutation.mutate()}
              disabled={discardMutation.isPending}
            >
              {discardMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Descartando...</>
              ) : (
                'Confirmar Descarte'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
